/**
 * CustomerGridCard - 고객 그리드 뷰 카드 컴포넌트
 *
 * 카드형 레이아웃으로 고객 정보를 간결하게 표시
 */

import {
  Star,
  StarOff,
  Phone,
  MoreVertical,
  Cake,
  FileText,
  User,
  Clock,
  PhoneCall,
  Calendar,
  Pencil,
  Ban,
  CalendarCheck,
  AlertTriangle,
  MessageSquare,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  formatPhoneNumber,
  formatBirthDate,
  getDisplayName,
} from "@/components/customer";
import { Checkbox } from "@/components/ui/checkbox";
import type { CustomerListCardProps } from "./CustomerListCard";
import type { Customer } from "@/types/customer";

function getRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return "오늘";
  if (days === 1) return "어제";
  if (days < 7) return `${days}일 전`;
  if (days < 30) return `${Math.floor(days / 7)}주 전`;
  if (days < 365) return `${Math.floor(days / 30)}개월 전`;
  return `${Math.floor(days / 365)}년 전`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

// 1시간 이상이면 총 분을 항상 보조 표시 (재계산 불편 회피)
function formatDuration(seconds: number): string {
  if (seconds <= 0) return "0분";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) {
    const hourPart = m > 0 ? `${h}시간 ${m}분` : `${h}시간`;
    return `${hourPart} (${h * 60 + m}분)`;
  }
  return `${m}분`;
}

function getNotesCount(customer: Customer): number {
  return customer.memoCount ?? 0;
}

function getMemoPreview(customer: Customer): string | null {
  return customer.latestMemoContent || null;
}

export function CustomerGridCard({
  customer,
  onSelect,
  onToggleStar,
  onEdit,
  onDelete,
  isSelected,
  isChecked = false,
  onCheck,
}: CustomerListCardProps) {
  const notePreview = getMemoPreview(customer);
  const notesCount = getNotesCount(customer);
  const displayInfo = getDisplayName(customer);

  return (
    <div
      className={cn(
        "border rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors flex flex-col gap-3 relative",
        isSelected && "ring-2 ring-primary bg-muted/40",
        isChecked && "bg-primary/5",
      )}
      onClick={() => onSelect(customer)}
    >
      {/* 체크박스 오버레이 */}
      {onCheck && (
        <div
          className="absolute top-2 left-2 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isChecked}
            onCheckedChange={(checked) =>
              onCheck(customer.id, checked === true)
            }
            aria-label={`${displayInfo.name} 선택`}
          />
        </div>
      )}

      {/* 헤더: 이름 + 액션 */}
      <div
        className={cn(
          "flex items-start justify-between gap-2",
          onCheck && "pl-6",
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <span className="font-semibold text-sm truncate">
                {displayInfo.name}
              </span>
              {displayInfo.isAi && (
                <Badge
                  variant="outline"
                  className="text-[9px] px-1 py-0 text-violet-600 border-violet-200 bg-violet-50"
                >
                  AI
                </Badge>
              )}
              {customer.starred && (
                <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500 flex-shrink-0" />
              )}
              {customer.gender && (
                <span className="text-[10px] text-muted-foreground">
                  {customer.gender === "MALE"
                    ? "남"
                    : customer.gender === "FEMALE"
                      ? "여"
                      : ""}
                </span>
              )}
            </div>
            {customer.tier && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 mt-0.5">
                {customer.tier}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              onToggleStar(customer.id);
            }}
          >
            {customer.starred ? (
              <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
            ) : (
              <StarOff className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreVertical className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(customer)}>
                <Pencil className="w-3.5 h-3.5 mr-2" />
                수정
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => onDelete(customer)}
              >
                삭제
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* 연락처 + 생일 */}
      <div className="space-y-1 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="font-mono">
            {formatPhoneNumber(customer.phone) || "-"}
          </span>
        </div>
        {customer.birthDate && (
          <div className="flex items-center gap-1.5">
            <Cake className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{formatBirthDate(customer.birthDate)}</span>
          </div>
        )}
      </div>

      {/* 통계 배지 */}
      <div className="flex flex-wrap gap-1">
        {customer.totalCallCount != null && (
          <Badge
            variant="secondary"
            className="text-[10px] px-1.5 py-0 font-normal"
          >
            <PhoneCall className="w-3 h-3 mr-0.5" />
            상담 {customer.totalCallCount}
            {(customer.totalCallDurationSeconds ?? 0) > 0 && (
              <span className="ml-0.5 text-muted-foreground">
                ({formatDuration(customer.totalCallDurationSeconds!)})
              </span>
            )}
          </Badge>
        )}
        {customer.visitCount != null && (
          <Badge
            variant="secondary"
            className="text-[10px] px-1.5 py-0 font-normal text-success"
          >
            <CalendarCheck className="w-3 h-3 mr-0.5" />
            방문 {customer.visitCount}
          </Badge>
        )}
        {(customer.noShowCount ?? 0) > 0 && (
          <Badge
            variant="secondary"
            className="text-[10px] px-1.5 py-0 font-normal text-warning bg-warning/10"
          >
            <AlertTriangle className="w-3 h-3 mr-0.5" />
            노쇼 {customer.noShowCount}
          </Badge>
        )}
        {notesCount > 0 && (
          <Badge
            variant="secondary"
            className="text-[10px] px-1.5 py-0 font-normal"
          >
            <MessageSquare className="w-3 h-3 mr-0.5" />
            메모 {notesCount}
          </Badge>
        )}
        {customer.lifecycleStage && (
          <Badge
            variant="outline"
            className={cn(
              "text-[10px] px-1.5 py-0",
              customer.lifecycleStage === "VIP" &&
                "text-purple-700 border-purple-200 bg-purple-50",
              customer.lifecycleStage === "CUSTOMER" &&
                "text-success border-success/30 bg-success/10",
              customer.lifecycleStage === "PROSPECT" &&
                "text-muted-foreground border-border bg-muted",
              customer.lifecycleStage === "CHURNED" &&
                "text-destructive border-destructive/30 bg-destructive/10",
            )}
          >
            {customer.lifecycleStage === "VIP"
              ? "VIP"
              : customer.lifecycleStage === "CUSTOMER"
                ? "고객"
                : customer.lifecycleStage === "PROSPECT"
                  ? "잠재"
                  : customer.lifecycleStage === "CHURNED"
                    ? "휴면"
                    : customer.lifecycleStage}
          </Badge>
        )}
      </div>

      {/* 다음 예약 / 차단 */}
      {(customer.nextAppointmentDate || customer.bookingBlocked) && (
        <div className="flex flex-wrap gap-1">
          {customer.nextAppointmentDate && (
            <Badge
              variant="outline"
              className="text-[10px] px-1.5 py-0 font-normal text-info border-info/30"
            >
              <Calendar className="w-3 h-3 mr-0.5" />
              다음예약{" "}
              {new Date(customer.nextAppointmentDate).toLocaleDateString(
                "ko-KR",
                { month: "numeric", day: "numeric" },
              )}
            </Badge>
          )}
          {customer.bookingBlocked && (
            <Badge
              variant="destructive"
              className="text-[10px] px-1.5 py-0 font-normal"
            >
              <Ban className="w-3 h-3 mr-0.5" />
              예약차단
            </Badge>
          )}
        </div>
      )}

      {/* 메모 미리보기 (호버 시 전체 메모 표시) */}
      {notePreview && (
        <TooltipProvider>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <div className="flex items-start gap-1.5 p-1.5 rounded bg-muted/30 hover:bg-muted/50 transition-colors">
                <FileText className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-xs text-foreground/80 line-clamp-2 text-left whitespace-pre-wrap">
                  {notePreview}
                </p>
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="max-w-sm max-h-60 overflow-y-auto"
            >
              <div className="space-y-2">
                <p className="text-sm break-words whitespace-pre-wrap">
                  {notePreview}
                </p>
                {customer.latestMemoCreatedAt && (
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(customer.latestMemoCreatedAt).toLocaleDateString(
                      "ko-KR",
                    )}
                  </p>
                )}
                {notesCount > 1 && (
                  <p className="text-[10px] text-muted-foreground">
                    고객 상세에서 전체 {notesCount}개 메모 확인
                  </p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* 하단: 등록일 + 최근상담 */}
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground/70 mt-auto pt-1 border-t flex-wrap">
        <Clock className="w-3 h-3" />
        <span>
          등록 {formatDate(customer.createdAt)} (
          {getRelativeTime(customer.createdAt)})
        </span>
        {customer.lastCallDate && (
          <>
            <span className="mx-0.5">|</span>
            <PhoneCall className="w-3 h-3" />
            <span>최근상담 {getRelativeTime(customer.lastCallDate)}</span>
          </>
        )}
      </div>
    </div>
  );
}
